import { css } from 'styled-components';
import { ifProp } from 'styled-tools';

function cssUnitFromCS(value: any) {
  // If value is a string that is not a straight number, then we assume it includes
  // the unit, is a percentage, or is a special string like "auto".
  if (isNaN(value)) {
    return value;
  }

  // Otherwise we add rem the value.
  return `${value}rem`;
}

function hasValue(value: any) {
  // this is a very dated function and should not be called going forward (just use !!)
  return typeof value !== 'undefined' && value !== null;
}

// The Promotion Banners have unique breakpoints due to the tablet image being
// used for any screen smaller than 1200px. The other blocks should use normal
// tablet breakpoints.
interface BannerBlockProps {
  $blockStyle?: any;
  $promoBanners?: boolean;
}

const bannerBlockStylesCss = css<BannerBlockProps>`
  ${({ $blockStyle, theme }) => css`
    font-size: 1rem;
    font-weight: 300;

    // Background
    ${ifProp(
      '$blockStyle.background?.backgroundColor',
      css`
        background-color: ${$blockStyle.background?.backgroundColor};
      `
    )}

    ${ifProp(
      '$blockStyle.background?.backgroundImage.url',
      css`
        background-image: url(' ${$blockStyle.background?.backgroundImage.url}');
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;

        ${theme.breakpoints.up('md')} {
          background-size: cover;
        }
      `
    )}

  // Borders
  ${ifProp(
      '$blockStyle.borders.applyBorder',
      css`
        border: 0.0625rem solid ${theme.colors.borderGray};
      `
    )}

  ${ifProp(
      '$blockStyle.borders.applyBoxShadow',
      css`
        box-shadow:
          0 24px 38px 3px rgba(0, 0, 0, 0.65),
          0 9px 36px 8px rgba(0, 0, 0, 0.65),
          0 11px 15px -7px rgba(0, 0, 0, 0.65);
      `
    )}

  // Max width
  ${hasValue($blockStyle?.maxWidth?.desktopMaxWidth)
      ? css`
          margin-left: auto;
          margin-right: auto;
          max-width: ${cssUnitFromCS($blockStyle.maxWidth.desktopMaxWidth)};
          width: 100%;
        `
      : ''}

  ${hasValue($blockStyle?.maxWidth?.tabletMaxWidth)
      ? css`
          @media (min-width: ${(props) => props.theme.breakpoints.sm}) and (max-width: 1199px) {
            margin-left: auto;
            margin-right: auto;
            max-width: ${cssUnitFromCS($blockStyle.maxWidth.tabletMaxWidth)};
            width: 100%;
          }
        `
      : ''}

  ${hasValue($blockStyle?.maxWidth?.mobileMaxWidth)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
            margin-left: auto;
            margin-right: auto;
            max-width: ${cssUnitFromCS($blockStyle.maxWidth.mobileMaxWidth)};
            width: 100%;
          }
        `
      : ''}

  ${hasValue($blockStyle?.maxWidth?.xsMobileMaxWidth)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
            margin-left: auto;
            margin-right: auto;
            max-width: ${cssUnitFromCS($blockStyle.maxWidth.xsMobileMaxWidth)};
            width: 100%;
          }
        `
      : ''}

  // Spacing
  // Margin Top
  ${hasValue($blockStyle?.spacing?.marginTop?.desktopMarginTop)
      ? css`
          margin-top: ${cssUnitFromCS($blockStyle.spacing.marginTop.desktopMarginTop)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginTop?.tabletMarginTop)
      ? css`
          @media (min-width: ${(props) => props.theme.breakpoints.sm}) and (max-width: 1199px) {
            margin-top: ${cssUnitFromCS($blockStyle.spacing.marginTop.tabletMarginTop)};
          }
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginTop?.mobileMarginTop)
      ? css`
          @media (max-width: 575px) {
            margin-top: ${cssUnitFromCS($blockStyle.spacing.marginTop.mobileMarginTop)};
          }
        `
      : ''}

  // Margin Bottom
  ${hasValue($blockStyle?.spacing?.marginBottom?.desktopMarginBottom)
      ? css`
          margin-bottom: ${cssUnitFromCS($blockStyle.spacing.marginBottom.desktopMarginBottom)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginBottom?.tabletMarginBottom)
      ? css`
          @media (min-width: ${(props) => props.theme.breakpoints.sm}) and (max-width: 1199px) {
            margin-bottom: ${cssUnitFromCS($blockStyle.spacing.marginBottom.tabletMarginBottom)};
          }
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginBottom?.mobileMarginBottom)
      ? css`
          @media (max-width: 575px) {
            margin-bottom: ${cssUnitFromCS($blockStyle.spacing.marginBottom.mobileMarginBottom)};
          }
        `
      : ''}

  // Margin Left
  ${hasValue($blockStyle?.spacing?.marginLeft?.desktopMarginLeft)
      ? css`
          margin-left: ${cssUnitFromCS($blockStyle.spacing.marginLeft.desktopMarginLeft)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginLeft?.tabletMarginLeft)
      ? css`
          @media ((min-width: ${(props) =>
              props.theme.breakpoints.sm}}) and (max-width: ) { margin-left: ${cssUnitFromCS(
              $blockStyle.spacing.marginLeft.tabletMarginLeft
            )}; };;
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginLeft?.mobileMarginLeft)
      ? css`
          @media (max-width: 575px) {
            margin-left: ${cssUnitFromCS($blockStyle.spacing.marginLeft.mobileMarginLeft)};
          }
        `
      : ''}

  // Margin Right
  ${hasValue($blockStyle?.spacing?.marginRight?.desktopMarginRight)
      ? css`
          margin-right: ${cssUnitFromCS($blockStyle.spacing.marginRight.desktopMarginRight)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginRight?.tabletMarginRight)
      ? css`
          @media ((min-width: ${(props) =>
              props.theme.breakpoints.sm}}) and (max-width: ) { margin-right: ${cssUnitFromCS(
              $blockStyle.spacing.marginRight.tabletMarginRight
            )}; };;
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.marginRight?.mobileMarginRight)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.sm}}) {
            margin-right: ${cssUnitFromCS($blockStyle.spacing.marginRight.mobileMarginRight)};
          }
        `
      : ''}

  // Padding Top
  ${hasValue($blockStyle?.spacing?.paddingTop?.desktopPaddingTop)
      ? css`
          padding-top: ${cssUnitFromCS($blockStyle.spacing.paddingTop.desktopPaddingTop)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingTop?.tabletPaddingTop)
      ? css`
          @media ((min-width: ${(props) =>
              props.theme.breakpoints.sm}}) and (max-width: ) { padding-top: ${cssUnitFromCS(
              $blockStyle.spacing.paddingTop.tabletPaddingTop
            )}; };;
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingTop?.mobilePaddingTop)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.sm}}) {
            padding-top: ${cssUnitFromCS($blockStyle.spacing.paddingTop.mobilePaddingTop)};
          }
        `
      : ''}

  // Padding Bottom
  ${hasValue($blockStyle?.spacing?.paddingBottom?.desktopPaddingBottom)
      ? css`
          padding-bottom: ${cssUnitFromCS($blockStyle.spacing.paddingBottom.desktopPaddingBottom)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingBottom?.tabletPaddingBottom)
      ? css`
          @media ((min-width: ${(props) =>
              props.theme.breakpoints.sm}}) and (max-width: ) { padding-bottom: ${cssUnitFromCS(
              $blockStyle.spacing.paddingBottom.tabletPaddingBottom
            )}; };;
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingBottom?.mobilePaddingBottom)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.sm}}) {
            padding-bottom: ${cssUnitFromCS($blockStyle.spacing.paddingBottom.mobilePaddingBottom)};
          }
        `
      : ''}

  // Padding Left
  ${hasValue($blockStyle?.spacing?.paddingLeft?.desktopPaddingLeft)
      ? css`
          padding-left: ${cssUnitFromCS($blockStyle.spacing.paddingLeft.desktopPaddingLeft)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingLeft?.tabletPaddingLeft)
      ? css`
          @media ((min-width: ${(props) =>
              props.theme.breakpoints.sm}}) and (max-width: ) { padding-left: ${cssUnitFromCS(
              $blockStyle.spacing.paddingLeft.tabletPaddingLeft
            )}; };;
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingLeft?.mobilePaddingLeft)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.sm}}) {
            padding-left: ${cssUnitFromCS($blockStyle.spacing.paddingLeft.mobilePaddingLeft)};
          }
        `
      : ''}

  // Padding Right
  ${hasValue($blockStyle?.spacing?.paddingRight?.desktopPaddingRight)
      ? css`
          padding-right: ${cssUnitFromCS($blockStyle.spacing.paddingRight.desktopPaddingRight)};
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingRight?.tabletPaddingRight)
      ? css`
          @media ((min-width: ${(props) =>
              props.theme.breakpoints.sm}}) and (max-width: ) { padding-right: ${cssUnitFromCS(
              $blockStyle.spacing.paddingRight.tabletPaddingRight
            )}; };;
        `
      : ''}

  ${hasValue($blockStyle?.spacing?.paddingRight?.mobilePaddingRight)
      ? css`
          @media (max-width: ${(props) => props.theme.breakpoints.sm}}) {
            padding-right: ${cssUnitFromCS($blockStyle.spacing.paddingRight.mobilePaddingRight)};
          }
        `
      : ''}

  // Custom CSS Rules
  ${($blockStyle?.customCss?.customCssRules?.value || [])
      .map(({ key, value }: { key: any; value: any }) => `${key}: ${value};`)
      .join('\n')}
  `}
`;

export default bannerBlockStylesCss;
